import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  input,
} from '@angular/core';

import { fromEvent as observableFromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';

import { WindowRef } from '../../../utils/window-ref';
import { RtTooltip } from '../../tooltip/tooltip.directive';

@Component({
  selector: 'rt-pagination-counter',
  templateUrl: './pagination-counter.component.html',
  styleUrls: ['./pagination-counter.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RtTooltip, TranslatePipe],
})
export class PaginationCounterComponent implements OnChanges, OnInit, OnDestroy {
  public readonly total = input<string | number>(undefined!);
  public readonly start = input<number>(undefined!);
  public readonly end = input<number>(undefined!);
  public readonly label = input<string>('');
  public readonly responsive = input<boolean>(undefined!);

  @ViewChild('fullTextTpl', { static: true }) public fullTextTpl: any;
  @ViewChild('shortenedTpl', { static: true }) public shortenedTpl: any;

  public tooltipMessage: string;
  public activeTpl: TemplateRef<any>;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private i18n: I18nService,
    private windowRef: WindowRef,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    this.tooltipMessage = `${this.start()} - ${this.end()} ${this.i18n.translate('rtu.common.of')} ${this.total()} ${this.label()}`;
  }

  public ngOnInit(): void {
    this.activeTpl = this.getActiveTemplate();
    observableFromEvent(this.windowRef.nativeWindow, 'resize')
      .pipe(
        debounceTime(50),
        map(() => this.getActiveTemplate()),
        distinctUntilChanged(),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe((tpl: TemplateRef<any>) => {
        this.activeTpl = tpl;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private getActiveTemplate(): TemplateRef<any> {
    return this.responsive() && this.windowRef.nativeWindow.innerWidth < 1300
      ? this.shortenedTpl
      : this.fullTextTpl;
  }
}
